import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  addEmployeeAPI,
  updateEmployeeAPI,
  getEmployeesListAPI,
  getEmployeesAPI,
  getEmployeeDetailAPI,
  getCompanyEmployeeInfoAPI,
  importEmployeesCsvAPI,
  deleteEmployeeAPI,
  getEmployeeSessionsAPI,
  getSessionAssessmentLinkAPI,
  getSessionDocumentLinkAPI,
  getSessionAssignmentResultAPI,
  getEmployeeAssignmentsAPI,
  getEmployeeDocumentsAPI,
  getAssignmentQuestionsAPI,
  submitAssignmentAnswersAPI,
  getAssignmentAnswersAPI,getEmployeeToolsAPI,
  lockCompanyEmployeeAPI,
} from "./employeesAPI";

// ✅ Add
export const addEmployee = createAsyncThunk(
  "employees/add",
  async ({ companyId, data }, { rejectWithValue }) => {
    try {
      const response = await addEmployeeAPI({ companyId, data });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to add employee");
    }
  }
);

// ✅ Update
export const updateEmployee = createAsyncThunk(
  "employees/update",
  async ({ companyId, employeeId, data }, { rejectWithValue }) => {
    try {
      const response = await updateEmployeeAPI({ companyId, employeeId, data });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update employee");
    }
  }
);

// ✅ Delete
export const deleteEmployee = createAsyncThunk(
  "employees/delete",
  async ({ company_id, employee_id }, { rejectWithValue }) => {
    try {
      const response = await deleteEmployeeAPI({ company_id, employee_id });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete employee");
    }
  }
);

// ✅ List (with pagination)
export const getEmployeesList = createAsyncThunk(
  "employees/list",
  async (params, { rejectWithValue }) => {
    try {
      const { companyId, page = 1, limit = 10, search = "", sort, order } = params || {};
      const response = await getEmployeesListAPI({ companyId, page, limit, search, sort, order });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch employees");
    }
  }
);

// ✅ Get All Employees (simple get)
export const getEmployees = createAsyncThunk(
  "employees/getAll",
  async (params, { rejectWithValue }) => {
    try {
      const response = await getEmployeesAPI(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch employees");
    }
  }
);

// ✅ Detail
export const getEmployeeDetail = createAsyncThunk(
  "employees/detail",
  async ({ companyId, employeeId }, { rejectWithValue }) => {
    try {
      const response = await getEmployeeDetailAPI({ companyId, employeeId });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch employee detail");
    }
  }
);

// ✅ Get Company Employee Info
export const getCompanyEmployeeInfo = createAsyncThunk(
  "employees/companyEmployeeInfo",
  async ({ companyEmployeeId }, { rejectWithValue }) => {
    try {
      const response = await getCompanyEmployeeInfoAPI({ companyEmployeeId });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch company employee info");
    }
  }
);

// ✅ Import CSV
export const importEmployeesCsv = createAsyncThunk(
  "employees/importCsv",
  async ({ companyId, formData }, { rejectWithValue }) => {
    try {
      const response = await importEmployeesCsvAPI({ companyId, formData });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to import employees");
    }
  }
);

// ✅ Get Employee Sessions
export const getEmployeeSessions = createAsyncThunk(
  "employees/sessions",
  async ({ employeeId, page, limit, search ,view}, { rejectWithValue }) => {
    try {
      const response = await getEmployeeSessionsAPI({ employeeId, page, limit, search ,view});
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch employee sessions");
    }
  }
);

// ✅ Get Session Assessment Link
export const getSessionAssessmentLink = createAsyncThunk(
  "employees/sessionAssessmentLink",
  async ({ sessionId, employeeId }, { rejectWithValue }) => {
    try {
      const response = await getSessionAssessmentLinkAPI({ sessionId, employeeId });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch assessment link");
    }
  }
);

// ✅ Get Session Document Link
export const getSessionDocumentLink = createAsyncThunk(
  "employees/sessionDocumentLink",
  async ({ sessionId, employeeId }, { rejectWithValue }) => {
    try {
      const response = await getSessionDocumentLinkAPI({ sessionId, employeeId });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch document link");
    }
  }
);

// ✅ Get Session Assignment Result
export const getSessionAssignmentResult = createAsyncThunk(
  "employees/sessionAssignmentResult",
  async ({ sessionId, employeeId }, { rejectWithValue }) => {
    try {
      const response = await getSessionAssignmentResultAPI({ sessionId, employeeId });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch assignment result");
    }
  }
);

// ✅ Get Employee Assignments (My Assignments)
export const getEmployeeAssignments = createAsyncThunk(
  "employees/assignments",
  async ({ employeeId, page, limit, search ,view}, { rejectWithValue }) => {
    try {
      const response = await getEmployeeAssignmentsAPI({ employeeId, page, limit, search ,view });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch employee assignments");
    }
  }
);

// ✅ Get Employee Documents (My Documents)
export const getEmployeeDocuments = createAsyncThunk(
  "employees/documents",
  async ({ employeeId, page, limit, search }, { rejectWithValue }) => {
    try {
      const response = await getEmployeeDocumentsAPI({ employeeId, page, limit, search });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch employee documents");
    }
  }
);

// ✅ Get Employee Tools (My Tools)
export const getEmployeeTools = createAsyncThunk(
  "employees/tools",
  async ({ employeeId, page, limit, search } = {}, { rejectWithValue }) => {
    try {
      const response = await getEmployeeToolsAPI({ employeeId, page, limit, search });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch employee tools");
    }
  }
);

// ✅ Get Assignment Questions
export const getAssignmentQuestions = createAsyncThunk(
  "employees/assignmentQuestions",
  async ({ sessionAssignmentId }, { rejectWithValue }) => {
    try {
      const response = await getAssignmentQuestionsAPI({ sessionAssignmentId });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch assignment questions");
    }
  }
);

// ✅ Submit Assignment Answers
export const submitAssignmentAnswers = createAsyncThunk(
  "employees/submitAssignmentAnswers",
  async ({ data }, { rejectWithValue }) => {
    try {
      const response = await submitAssignmentAnswersAPI({ data });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to submit assignment answers");
    }
  }
);

// ✅ Get Assignment Answers
export const getAssignmentAnswers = createAsyncThunk(
  "employees/assignmentAnswers",
  async ({ sessionAssignmentId }, { rejectWithValue }) => {
    try {
      const response = await getAssignmentAnswersAPI({ sessionAssignmentId });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch assignment answers");
    }
  }
);

// ✅ Lock / Unlock Company Employee
export const lockCompanyEmployee = createAsyncThunk(
  "employees/lockCompanyEmployee",
  async ({ company_employee_id, status }, { rejectWithValue }) => {
    try {
      const response = await lockCompanyEmployeeAPI({ company_employee_id, status });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to lock/unlock employee");
    }
  }
);

const initialState = {
  addEmployee: { data: null, loading: false, error: null },
  updateEmployee: { data: null, loading: false, error: null },
  deleteEmployee: { data: null, loading: false, error: null },
  getEmployeesList: { data: [], total: 0, loading: false, error: null },
  getEmployees: { data: [], total: 0, loading: false, error: null },
  getEmployeeDetail: { data: null, loading: false, error: null },
  getCompanyEmployeeInfo: { data: null, loading: false, error: null },
  importEmployeesCsv: { data: null, loading: false, error: null },
  getEmployeeSessions: { data: [], total: 0, loading: false, error: null },
  getSessionAssessmentLink: { data: null, loading: false, error: null },
  getSessionDocumentLink: { data: null, loading: false, error: null },
  getSessionAssignmentResult: { data: null, loading: false, error: null },
  getEmployeeAssignments: { data: [], loading: false, error: null },
  getEmployeeDocuments: { data: { data: [], pagination: {} }, loading: false, error: null },
  getEmployeeTools: { data: [], loading: false, error: null },
  getAssignmentQuestions: { data: null, loading: false, error: null },
  submitAssignmentAnswers: { data: null, loading: false, error: null },
  getAssignmentAnswers: { data: null, loading: false, error: null },
  lockCompanyEmployee: { data: null, loading: false, error: null },
};

const employeesSlice = createSlice({
  name: "employees",
  initialState,
  reducers: {
    clearAddEmployee: (state) => {
      state.addEmployee.data = null;
      state.addEmployee.error = null;
    },
    clearUpdateEmployee: (state) => {
      state.updateEmployee.data = null;
      state.updateEmployee.error = null;
    },
    clearDeleteEmployee: (state) => {
      state.deleteEmployee.data = null;
      state.deleteEmployee.error = null;
    },
    clearEmployeeDetail: (state) => {
      state.getEmployeeDetail.data = null;
      state.getEmployeeDetail.error = null;
    },
    clearCompanyEmployeeInfo: (state) => {
      state.getCompanyEmployeeInfo.data = null;
      state.getCompanyEmployeeInfo.error = null;
    },
    clearImportEmployeesCsv: (state) => {
      state.importEmployeesCsv.data = null;
      state.importEmployeesCsv.error = null;
    },
    clearGetEmployees: (state) => {
      state.getEmployees.data = [];
      state.getEmployees.error = null;
    },
    clearEmployeeSessions: (state) => {
      state.getEmployeeSessions.data = [];
      state.getEmployeeSessions.error = null;
    },
    clearSessionAssessmentLink: (state) => {
      state.getSessionAssessmentLink.data = null;
      state.getSessionAssessmentLink.error = null;
    },
    clearSessionDocumentLink: (state) => {
      state.getSessionDocumentLink.data = null;
      state.getSessionDocumentLink.error = null;
    },
    clearSessionAssignmentResult: (state) => {
      state.getSessionAssignmentResult.data = null;
      state.getSessionAssignmentResult.error = null;
    },
    clearEmployeeAssignments: (state) => {
      state.getEmployeeAssignments.data = [];
      state.getEmployeeAssignments.error = null;
    },
    clearEmployeeDocuments: (state) => {
      state.getEmployeeDocuments.data = { data: [], pagination: {} };
      state.getEmployeeDocuments.error = null;
    },
    clearAssignmentQuestions: (state) => {
      state.getAssignmentQuestions.data = null;
      state.getAssignmentQuestions.error = null;
    },
    clearSubmitAssignmentAnswers: (state) => {
      state.submitAssignmentAnswers.data = null;
      state.submitAssignmentAnswers.error = null;
    },
    clearAssignmentAnswers: (state) => {
      state.getAssignmentAnswers.data = null;
      state.getAssignmentAnswers.error = null;
    },
    clearEmployeeTools: (state) => {
      state.getEmployeeTools.data = [];
      state.getEmployeeTools.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Add
      .addCase(addEmployee.pending, (state) => {
        state.addEmployee.loading = true;
        state.addEmployee.error = null;
      })
      .addCase(addEmployee.fulfilled, (state, action) => {
        state.addEmployee.loading = false;
        state.addEmployee.data = action.payload;
      })
      .addCase(addEmployee.rejected, (state, action) => {
        state.addEmployee.loading = false;
        state.addEmployee.error = action.payload;
      })

      // Update
      .addCase(updateEmployee.pending, (state) => {
        state.updateEmployee.loading = true;
        state.updateEmployee.error = null;
      })
      .addCase(updateEmployee.fulfilled, (state, action) => {
        state.updateEmployee.loading = false;
        state.updateEmployee.data = action.payload;
      })
      .addCase(updateEmployee.rejected, (state, action) => {
        state.updateEmployee.loading = false;
        state.updateEmployee.error = action.payload;
      })

      // Delete
      .addCase(deleteEmployee.pending, (state) => {
        state.deleteEmployee.loading = true;
        state.deleteEmployee.error = null;
      })
      .addCase(deleteEmployee.fulfilled, (state, action) => {
        state.deleteEmployee.loading = false;
        state.deleteEmployee.data = action.payload;
      })
      .addCase(deleteEmployee.rejected, (state, action) => {
        state.deleteEmployee.loading = false;
        state.deleteEmployee.error = action.payload;
      })

      // List (with pagination)
      .addCase(getEmployeesList.pending, (state) => {
        state.getEmployeesList.loading = true;
        state.getEmployeesList.error = null;
      })
      .addCase(getEmployeesList.fulfilled, (state, action) => {
        state.getEmployeesList.loading = false;
        state.getEmployeesList.data = action.payload?.data || [];
        state.getEmployeesList.total =
          action.payload?.pagination?.total ||
          action.payload?.count ||
          (Array.isArray(action.payload?.data) ? action.payload.data.length : 0);
      })
      .addCase(getEmployeesList.rejected, (state, action) => {
        state.getEmployeesList.loading = false;
        state.getEmployeesList.error = action.payload;
      })

      // Get All Employees (without pagination)
      .addCase(getEmployees.pending, (state) => {
        state.getEmployees.loading = true;
        state.getEmployees.error = null;
      })
      .addCase(getEmployees.fulfilled, (state, action) => {
        state.getEmployees.loading = false;
        state.getEmployees.data = action.payload?.data || [];
        state.getEmployees.total =
          action.payload?.total ||
          action.payload?.count ||
          (Array.isArray(action.payload?.data) ? action.payload.data.length : 0);
      })
      .addCase(getEmployees.rejected, (state, action) => {
        state.getEmployees.loading = false;
        state.getEmployees.error = action.payload;
      })

      // Detail
      .addCase(getEmployeeDetail.pending, (state) => {
        state.getEmployeeDetail.loading = true;
        state.getEmployeeDetail.error = null;
      })
      .addCase(getEmployeeDetail.fulfilled, (state, action) => {
        state.getEmployeeDetail.loading = false;
        state.getEmployeeDetail.data = action.payload?.data || action.payload;
      })
      .addCase(getEmployeeDetail.rejected, (state, action) => {
        state.getEmployeeDetail.loading = false;
        state.getEmployeeDetail.error = action.payload;
      })

      // Company Employee Info
      .addCase(getCompanyEmployeeInfo.pending, (state) => {
        state.getCompanyEmployeeInfo.loading = true;
        state.getCompanyEmployeeInfo.error = null;
      })
      .addCase(getCompanyEmployeeInfo.fulfilled, (state, action) => {
        state.getCompanyEmployeeInfo.loading = false;
        state.getCompanyEmployeeInfo.data = action.payload?.data || action.payload;
      })
      .addCase(getCompanyEmployeeInfo.rejected, (state, action) => {
        state.getCompanyEmployeeInfo.loading = false;
        state.getCompanyEmployeeInfo.error = action.payload;
      })

      // Import CSV
      .addCase(importEmployeesCsv.pending, (state) => {
        state.importEmployeesCsv.loading = true;
        state.importEmployeesCsv.error = null;
      })
      .addCase(importEmployeesCsv.fulfilled, (state, action) => {
        state.importEmployeesCsv.loading = false;
        state.importEmployeesCsv.data = action.payload;
      })
      .addCase(importEmployeesCsv.rejected, (state, action) => {
        state.importEmployeesCsv.loading = false;
        state.importEmployeesCsv.error = action.payload;
      })

      // Get Employee Sessions
      .addCase(getEmployeeSessions.pending, (state) => {
        state.getEmployeeSessions.loading = true;
        state.getEmployeeSessions.error = null;
      })
      .addCase(getEmployeeSessions.fulfilled, (state, action) => {
        state.getEmployeeSessions.loading = false;
        state.getEmployeeSessions.data = action.payload?.data || action.payload || [];
        state.getEmployeeSessions.total = action.payload?.pagination?.total || 0;
      })
      .addCase(getEmployeeSessions.rejected, (state, action) => {
        state.getEmployeeSessions.loading = false;
        state.getEmployeeSessions.error = action.payload;
      })

      // Get Session Assessment Link
      .addCase(getSessionAssessmentLink.pending, (state) => {
        state.getSessionAssessmentLink.loading = true;
        state.getSessionAssessmentLink.error = null;
      })
      .addCase(getSessionAssessmentLink.fulfilled, (state, action) => {
        state.getSessionAssessmentLink.loading = false;
        state.getSessionAssessmentLink.data = action.payload?.data || action.payload;
      })
      .addCase(getSessionAssessmentLink.rejected, (state, action) => {
        state.getSessionAssessmentLink.loading = false;
        state.getSessionAssessmentLink.error = action.payload;
      })

      // Get Session Document Link
      .addCase(getSessionDocumentLink.pending, (state) => {
        state.getSessionDocumentLink.loading = true;
        state.getSessionDocumentLink.error = null;
      })
      .addCase(getSessionDocumentLink.fulfilled, (state, action) => {
        state.getSessionDocumentLink.loading = false;
        state.getSessionDocumentLink.data = action.payload?.data || action.payload;
      })
      .addCase(getSessionDocumentLink.rejected, (state, action) => {
        state.getSessionDocumentLink.loading = false;
        state.getSessionDocumentLink.error = action.payload;
      })

      // Get Session Assignment Result
      .addCase(getSessionAssignmentResult.pending, (state) => {
        state.getSessionAssignmentResult.loading = true;
        state.getSessionAssignmentResult.error = null;
      })
      .addCase(getSessionAssignmentResult.fulfilled, (state, action) => {
        state.getSessionAssignmentResult.loading = false;
        state.getSessionAssignmentResult.data = action.payload?.data || action.payload;
      })
      .addCase(getSessionAssignmentResult.rejected, (state, action) => {
        state.getSessionAssignmentResult.loading = false;
        state.getSessionAssignmentResult.error = action.payload;
      })

      // Get Employee Assignments
      .addCase(getEmployeeAssignments.pending, (state) => {
        state.getEmployeeAssignments.loading = true;
        state.getEmployeeAssignments.error = null;
      })
      .addCase(getEmployeeAssignments.fulfilled, (state, action) => {
        state.getEmployeeAssignments.loading = false;
        state.getEmployeeAssignments.data = action.payload?.data || action.payload || [];
      })
      .addCase(getEmployeeAssignments.rejected, (state, action) => {
        state.getEmployeeAssignments.loading = false;
        state.getEmployeeAssignments.error = action.payload;
      })

      // Get Employee Documents
      .addCase(getEmployeeDocuments.pending, (state) => {
        state.getEmployeeDocuments.loading = true;
        state.getEmployeeDocuments.error = null;
      })
      .addCase(getEmployeeDocuments.fulfilled, (state, action) => {
        state.getEmployeeDocuments.loading = false;
        state.getEmployeeDocuments.data = action.payload || { data: [], pagination: {} };
      })
      .addCase(getEmployeeDocuments.rejected, (state, action) => {
        state.getEmployeeDocuments.loading = false;
        state.getEmployeeDocuments.error = action.payload;
      })

      // Get Employee Tools
      .addCase(getEmployeeTools.pending, (state) => {
        state.getEmployeeTools.loading = true;
        state.getEmployeeTools.error = null;
      })
      .addCase(getEmployeeTools.fulfilled, (state, action) => {
        state.getEmployeeTools.loading = false;
        state.getEmployeeTools.data = action.payload?.data || action.payload || [];
      })
      .addCase(getEmployeeTools.rejected, (state, action) => {
        state.getEmployeeTools.loading = false;
        state.getEmployeeTools.error = action.payload;
      })

      // Get Assignment Questions
      .addCase(getAssignmentQuestions.pending, (state) => {
        state.getAssignmentQuestions.loading = true;
        state.getAssignmentQuestions.error = null;
      })
      .addCase(getAssignmentQuestions.fulfilled, (state, action) => {
        state.getAssignmentQuestions.loading = false;
        state.getAssignmentQuestions.data = action.payload?.data || action.payload;
      })
      .addCase(getAssignmentQuestions.rejected, (state, action) => {
        state.getAssignmentQuestions.loading = false;
        state.getAssignmentQuestions.error = action.payload;
      })

      // Submit Assignment Answers
      .addCase(submitAssignmentAnswers.pending, (state) => {
        state.submitAssignmentAnswers.loading = true;
        state.submitAssignmentAnswers.error = null;
      })
      .addCase(submitAssignmentAnswers.fulfilled, (state, action) => {
        state.submitAssignmentAnswers.loading = false;
        state.submitAssignmentAnswers.data = action.payload;
      })
      .addCase(submitAssignmentAnswers.rejected, (state, action) => {
        state.submitAssignmentAnswers.loading = false;
        state.submitAssignmentAnswers.error = action.payload;
      })

      // Get Assignment Answers
      .addCase(getAssignmentAnswers.pending, (state) => {
        state.getAssignmentAnswers.loading = true;
        state.getAssignmentAnswers.error = null;
      })
      .addCase(getAssignmentAnswers.fulfilled, (state, action) => {
        state.getAssignmentAnswers.loading = false;
        state.getAssignmentAnswers.data = action.payload?.data || action.payload;
      })
      .addCase(getAssignmentAnswers.rejected, (state, action) => {
        state.getAssignmentAnswers.loading = false;
        state.getAssignmentAnswers.error = action.payload;
      });
      // Lock / Unlock Company Employee
      builder
      .addCase(lockCompanyEmployee.pending, (state) => {
        state.lockCompanyEmployee.loading = true;
        state.lockCompanyEmployee.error = null;
      })
      .addCase(lockCompanyEmployee.fulfilled, (state, action) => {
        state.lockCompanyEmployee.loading = false;
        state.lockCompanyEmployee.data = action.payload;
      })
      .addCase(lockCompanyEmployee.rejected, (state, action) => {
        state.lockCompanyEmployee.loading = false;
        state.lockCompanyEmployee.error = action.payload;
      });
  },
});

export const {
  clearAddEmployee,
  clearUpdateEmployee,
  clearDeleteEmployee,
  clearEmployeeDetail,
  clearCompanyEmployeeInfo,
  clearImportEmployeesCsv,
  clearGetEmployees,
  clearEmployeeSessions,
  clearSessionAssessmentLink,
  clearSessionDocumentLink,
  clearSessionAssignmentResult,
  clearEmployeeAssignments,
  clearEmployeeDocuments,
  clearEmployeeTools,
  clearAssignmentQuestions,
  clearSubmitAssignmentAnswers,
  clearAssignmentAnswers,
} = employeesSlice.actions;

export default employeesSlice.reducer;